import React from 'react';
import { useTranslation } from 'localization';

export interface DataListMobileViewProps<T = unknown> {
  /** Data items to render */
  data: T[];
  /** Template function to render each item as a card */
  cardTemplate: (item: T, index: number) => React.ReactNode;
  /** Message to show when data is empty */
  emptyMessage?: string;
  /** Whether data is loading */
  loading?: boolean;
  /** Number of skeleton cards to show when loading */
  skeletonCount?: number;
}

function DataListMobileView<T = unknown>({
  data,
  cardTemplate,
  emptyMessage,
  loading = false,
  skeletonCount = 5,
}: Readonly<DataListMobileViewProps<T>>) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="data-list-mobile-view data-list-mobile-view--loading">
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <div key={`skeleton-${index}`} className="data-list-mobile-card data-list-mobile-card--skeleton">
            <div className="skeleton-line skeleton-line--title" />
            <div className="skeleton-line skeleton-line--subtitle" />
            <div className="skeleton-line skeleton-line--body" />
          </div>
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="data-list-mobile-view data-list-mobile-view--empty">
        <div className="data-list-empty-message">
          <i className="pi pi-inbox" />
          <span>{emptyMessage || t('dataList.emptyMessage', 'No data available')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="data-list-mobile-view">
      {data.map((item, index) => (
        <div key={index} className="data-list-mobile-card-wrapper">
          {cardTemplate(item, index)}
        </div>
      ))}
    </div>
  );
}

export default DataListMobileView;
